/* @jsx h */
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignOffer, EmailBlock, EmailBlockType, EmailContent } from "../types";
import { defaultEmailBlock } from "../stub";
import { SampleCustomerPicker, Segmented } from "../shared";
import { EmailCanvas } from "./EmailCanvas";
import { EmailInspector } from "./EmailInspector";
import { EmailPalette } from "./EmailPalette";

type EmailEditorProps = {
  email: EmailContent;
  offer: CampaignOffer;
  onChange: (email: EmailContent) => void;
};

const HISTORY_LIMIT = 50;
// Edits closer together than this collapse into one undo step (typing).
const COALESCE_MS = 600;

export const EmailEditor = defineComponent<EmailEditorProps>({
  name: "EmailEditor",
  props: ["email", "offer", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const selectedId = ref<string | null>(null);
    const device = ref<"desktop" | "mobile">("desktop");

    const past = ref<EmailContent[]>([]);
    const future = ref<EmailContent[]>([]);
    let lastPush = 0;

    const commit = (next: EmailContent) => {
      const now = Date.now();
      if (now - lastPush > COALESCE_MS) {
        past.value = [...past.value.slice(-HISTORY_LIMIT + 1), props.email];
      }
      lastPush = now;
      future.value = [];
      props.onChange(next);
    };

    const undo = () => {
      const prev = past.value[past.value.length - 1];
      if (!prev) return;
      past.value = past.value.slice(0, -1);
      future.value = [props.email, ...future.value];
      lastPush = 0;
      props.onChange(prev);
    };

    const redo = () => {
      const [next, ...rest] = future.value;
      if (!next) return;
      future.value = rest;
      past.value = [...past.value, props.email];
      lastPush = 0;
      props.onChange(next);
    };

    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("input, textarea, select")) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      }
    };
    onMounted(() => window.addEventListener("keydown", onKey));
    onBeforeUnmount(() => window.removeEventListener("keydown", onKey));

    const selectedBlock = computed(() => props.email.blocks.find((b) => b.id === selectedId.value) ?? null);

    const setBlocks = (blocks: EmailBlock[]) => commit({ ...props.email, blocks });

    const addBlock = (type: EmailBlockType) => {
      const block = defaultEmailBlock(type);
      const blocks = [...props.email.blocks];
      const at = selectedId.value ? blocks.findIndex((b) => b.id === selectedId.value) + 1 : blocks.length;
      blocks.splice(at, 0, block);
      setBlocks(blocks);
      selectedId.value = block.id;
    };

    return () => (
      <div class="flex h-full min-h-0 overflow-hidden">
        <aside class="w-60 shrink-0 border-r border-base-300 bg-base-100">
          <EmailPalette
            theme={props.email.theme}
            onAdd={addBlock}
            onThemeChange={(theme) => commit({ ...props.email, theme })}
          />
        </aside>

        <section class="flex min-w-0 flex-1 flex-col">
          <div class="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-base-300 bg-base-100 px-3 py-2">
            <div class="flex items-center gap-1">
              <button type="button" class="btn btn-ghost btn-sm btn-square" disabled={!past.value.length} title={t("campaigns.studio_email.undo")} onClick={undo}>
                <span class="material-symbols-outlined text-lg">undo</span>
              </button>
              <button type="button" class="btn btn-ghost btn-sm btn-square" disabled={!future.value.length} title={t("campaigns.studio_email.redo")} onClick={redo}>
                <span class="material-symbols-outlined text-lg">redo</span>
              </button>
              <div class="ml-2 w-28">
                <Segmented
                  size="xs"
                  value={device.value}
                  options={[
                    { value: "desktop", label: "", icon: "desktop_windows" },
                    { value: "mobile", label: "", icon: "smartphone" },
                  ]}
                  onChange={(v) => (device.value = v as "desktop" | "mobile")}
                />
              </div>
            </div>
            <SampleCustomerPicker />
          </div>
          <div class="min-h-0 flex-1 overflow-auto">
            <EmailCanvas
              email={props.email}
              offer={props.offer}
              mobile={device.value === "mobile"}
              selectedId={selectedId.value}
              onSelect={(id) => (selectedId.value = id)}
              onBlocksChange={setBlocks}
            />
          </div>
        </section>

        <aside class="w-80 shrink-0 border-l border-base-300 bg-base-100">
          <EmailInspector
            email={props.email}
            block={selectedBlock.value}
            onEmailChange={(patch) => commit({ ...props.email, ...patch })}
            onBlockChange={(block) =>
              setBlocks(props.email.blocks.map((b) => (b.id === block.id ? block : b)))
            }
            onDeselect={() => (selectedId.value = null)}
          />
        </aside>
      </div>
    );
  },
});
